import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames/bind';

import type { PodcastEpisode, BlockNode } from 'types';
import Error from 'pages/_error.page';
import useFeature from 'shared/feature';
import { renderRichText } from 'shared/util';
import * as podcastEpisodeStore from 'store/podcast-episodes';
import * as podcastStore from 'store/podcasts';
import * as videoStore from 'store/videos';
import { useHeader } from 'components/Header';
import { useFooter } from 'components/Footer';
import { Loadable } from 'store/util';
import { exhaustive } from 'shared/util';
import PodcastEpisodeListing from 'components/PodcastEpisodeListing';
import { DisplayAd } from 'components/DisplayAd';
import Grid from 'components/Grid';
import CopyLinkButton from 'components/CopyLinkButton';
import Icon from 'components/Icon';
import { RecirculationCarousel } from 'components/RecirculationCarousel';
import { Meta } from './Meta';
import styles from './[episode].module.scss';

const AD_SLOT_1 = '/22673562521/Responsive_DisplayAds_ATF';
const AD_SLOT_2 = '/22673562521/Responsive_DisplayAds_BTF';

type DetailsProps = {
  episode: PodcastEpisode<'podcastShow'>;
};
const Details = ({ episode }: DetailsProps) => {
  return (
    <div className={styles.playerTitleDesc}>
      <div className={styles.wrapper}>
        <iframe
          title="Podcast Player"
          loading="lazy"
          className={styles.iframe}
          src={`https://embed.acast.com/${episode.podcastShow.acastShowId}/${episode.acastEpisodeId}`}
          frameBorder="0"
        ></iframe>
        <h1 className={styles.episodeTitle}>{episode.title}</h1>
        <div
          className={styles.episodeDescription}
          dangerouslySetInnerHTML={{ __html: episode.description }}
        />
        <div className={styles.socialIcons}>
          <CopyLinkButton clipboardText="https://therecount.com/" />
          <a href="https://twitter.com/therecount" target="_blank" rel="noreferrer">
            <Icon.Twitter color="#fff" />
          </a>
          <a href="https://www.facebook.com/TheRecount/" target="_blank" rel="noreferrer">
            <Icon.Facebook color="#fff" />
          </a>
        </div>
      </div>
    </div>
  );
};

type SubscribeLinksProps = {
  links: Array<{
    title: string;
    type: string;
    url: string;
  }>;
};
const SubscribeLinks = ({ links }: SubscribeLinksProps) => {
  const platformIcons: { [p: string]: string } = {
    apple: '/podcastServices/apple.svg',
    google: '/podcastServices/google.svg',
    spotify: '/podcastServices/spotify.svg',
  };

  return (
    <Grid container justifyContent="center" gap=".5rem" className={styles.subscribeLinks}>
      {links.map((l) => {
        const icon = platformIcons[l.type];
        const iconImg = icon ? (
          <img className={styles.icon} src={icon} height={12} width={12} />
        ) : null;

        return (
          <a key={l.url} className={styles.subscribeLink} href={l.url}>
            {iconImg} {l.title}
          </a>
        );
      })}
    </Grid>
  );
};

const Transcript = ({ transcript }: { transcript: Array<BlockNode> }) => {
  const [transcriptIsOpen, setTranscriptIsOpen] = useState(false);
  const transcriptStyles = classNames(
    styles.transcriptText,
    transcriptIsOpen ? styles.open : styles.collapsed,
  );
  const caretClass = classNames(styles.caret, transcriptIsOpen ? styles.caretUp : styles.caretDown);
  if (transcript) {
    return (
      <>
        <h3 className={styles.transcriptTitle} onClick={() => setTranscriptIsOpen((v) => !v)}>
          transcript
          <span className={caretClass}>
            <Icon.Caret color="#000" />
          </span>
        </h3>

        <div
          className={transcriptStyles}
          dangerouslySetInnerHTML={{
            __html: renderRichText(transcript),
          }}
        />
      </>
    );
  }

  return null;
};

interface PodcastEpisodePageProps {
  query: {
    slug: string;
    episode: string;
  };
}

const PodcastEpisodePage: React.FC<PodcastEpisodePageProps> = () => {
  const router = useRouter();
  const showSlug = router.query.show as string;
  const episodeSlug = router.query.episode as string;

  const feature = useFeature();
  const episodeL = podcastEpisodeStore.getBySlug(episodeSlug);

  const show_ = podcastStore.getBySlug(showSlug);

  const relatedVideosL = videoStore.getByShow({
    show: Loadable.map(show_, (s) => s.showId),
    limit: 12,
  });

  const Header = useHeader();
  const Footer = useFooter();

  if (!feature.isOn('recount-redesign')) {
    return <Error statusCode={404} />;
  }

  useEffect(() => {
    Loadable.forEach(episodeL, (e) => {
      if (e.podcastShow.slug !== showSlug) {
        window.history.replaceState(null, '', `/podcasts/${showSlug}/episode/${episodeSlug}`);
      }
    });
  }, []);

  switch (episodeL._tag) {
    case 'NotFound':
      return <Error statusCode={404} />;
    case 'NotLoaded':
      return null; // TODO loading state
    case 'Loaded': {
      let recirc = null; // TODO loading state
      Loadable.forEach(relatedVideosL, (vs) => {
        recirc = <RecirculationCarousel videos={vs} title="Watch Highlights" />;
      });

      const episode = episodeL.data;

      return (
        <>
          <Meta podcastEpisode={episode} />
          <Header />
          <Details episode={episode} />
          <div className={styles.wrapper}>
            <SubscribeLinks links={episode.podcastShow.subscribeLinks} />
            <Transcript transcript={episode.transcript} />
            <DisplayAd adSlot={AD_SLOT_1} currentPath={`/podcasts/${showSlug}/${episodeSlug}`} />
            <h3 className={styles.recentEpisodesTitle}>Recent Episodes</h3>
            <PodcastEpisodeListing slug={showSlug} expanded={true} excluded={[episode.id]} />
          </div>
          <div className={styles.recirculationConatiner}>{recirc}</div>
          <DisplayAd adSlot={AD_SLOT_2} currentPath={`/podcasts/${showSlug}/${episodeSlug}`} />
          <Footer />
        </>
      );
    }
    default:
      return exhaustive(episodeL);
  }
};

export default PodcastEpisodePage;
